import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CheckIcon from "@mui/icons-material/Check";
import "./MultiSelectChipInput.scss";

export interface MultiSelectChipInputProps {
  selected: string[];
  options: string[];
  placeholder?: string;
  searchPlaceholder?: string;
  onToggle: (value: string) => void;
  onSelectAll: () => void;
  onReset: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────

const MultiSelectChipInput: React.FC<MultiSelectChipInputProps> = ({
  selected,
  options,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  onToggle,
  onSelectAll,
  onReset,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const [tempSelected, setTempSelected] = useState<string[]>(selected);

  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Sync tempSelected with selected prop when dropdown opens or selected changes
  useEffect(() => {
    if (open) {
      setTempSelected(selected);
    }
  }, [open, selected]);

  const currentSelection = open ? tempSelected : selected;

  // ── Position portal under trigger ──────────────────────────────────────────
  const recalcPosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 260),
        zIndex: 99999,
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (open) {
      recalcPosition();
      requestAnimationFrame(() => {
        searchInputRef.current?.focus({ preventScroll: true });
      });
    }
  }, [open, recalcPosition]);

  useEffect(() => {
    if (!open) return;
    const handler = () => recalcPosition();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open, recalcPosition]);

  // ── Close on outside mousedown (capture phase) ──────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [open]);

  const allSelected = options.length > 0 && currentSelection.length === options.length;

  const filtered = options.filter(o =>
    o.toLowerCase().replace(/\s/g, "").includes(search.toLowerCase().replace(/\s/g, ""))
  );

  const handleToggleClick = (val: string) => {
    if (open) {
      setTempSelected(prev =>
        prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
      );
    } else {
      onToggle(val);
    }
  };

  const handleSelectAllClick = () => {
    if (open) {
      setTempSelected(prev =>
        prev.length === options.length ? [] : [...options]
      );
    } else {
      onSelectAll();
    }
  };

  const handleResetClick = () => {
    if (open) {
      setTempSelected([]);
    } else {
      onReset();
    }
  };

  const handleDone = () => {
    if (tempSelected.length === 0) {
      onReset();
    } else {
      const added = tempSelected.filter(x => !selected.includes(x));
      const removed = selected.filter(x => !tempSelected.includes(x));
      removed.forEach(val => onToggle(val));
      added.forEach(val => onToggle(val));
    }
    setOpen(false);
    setSearch("");
  };

  // ── Enter key: select first filtered option (not "All") ────────────────────
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Enter" && filtered.length > 0) {
      handleToggleClick(filtered[0]);
      setSearch("");
    }
  };

  // ── Chips ───────────────────────────────────────────────────────────────────
  const renderChips = () => {
    if (currentSelection.length === 0) return null;
    if (allSelected) {
      return (
        <span className="msc-chip">
          All ({options.length})
          <button
            type="button"
            className="msc-chip__remove"
            onMouseDown={e => { e.stopPropagation(); e.preventDefault(); handleResetClick(); }}
            title="Clear all"
          >
            ×
          </button>
        </span>
      );
    }
    return currentSelection.map(val => (
      <span key={val} className="msc-chip">
        <span className="msc-chip__text">
        {val}
        </span>
        <button
          type="button"
          className="msc-chip__remove"
          onMouseDown={e => { e.stopPropagation(); e.preventDefault(); handleToggleClick(val); }}
          title={`Remove ${val}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="#a0a0a0" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>
        </button>
      </span>
    ));
  };

  // ── Portal dropdown ─────────────────────────────────────────────────────────
  const dropdownContent = (
    <div
      ref={dropdownRef}
      className="msc-dropdown"
      style={dropdownStyle}
      onMouseDown={e => e.stopPropagation()}
    >
      {/* Search */}
      <div className="msc-dropdown__search-wrap">
        <div className="msc-dropdown__search-inner">
          <SearchIcon className="msc-dropdown__search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="msc-dropdown__search-input"
          />
        </div>
      </div>

      {/* List */}
      <div className="msc-dropdown__list">
        {/* All */}
        {options.length > 0 && (
          <div
            className="msc-dropdown__row msc-dropdown__row--all"
            onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleSelectAllClick(); }}
          >
            <input type="checkbox" checked={allSelected} readOnly className="msc-dropdown__checkbox" />
            <span className="msc-dropdown__opt-label msc-dropdown__opt-label--all">All</span>
          </div>
        )}

        {filtered.length > 0 ? (
          filtered.map(opt => (
            <div
              key={opt}
              className="msc-dropdown__row"
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleToggleClick(opt); }}
            >
              <input type="checkbox" checked={currentSelection.includes(opt)} readOnly className="msc-dropdown__checkbox" />
              <span className="msc-dropdown__opt-label">{opt}</span>
            </div>
          ))
        ) : (
          <div className="msc-dropdown__empty">No options found</div>
        )}
      </div>

      {/* Done */}
      <div className="msc-dropdown__footer">
        <button
          className="msc-dropdown__done-btn"
          onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleDone(); }}
        >
          <CheckIcon sx={{ fontSize: 16, color: "#3A809E" }} />
          Done
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Trigger */}
      <div
        ref={triggerRef}
        onMouseDown={e => {
          if ((e.target as HTMLElement).closest("button")) return;
          e.preventDefault();
          setOpen(prev => !prev);
        }}
        className="msc-trigger"
      >
        {currentSelection.length === 0 && (
          <span className="msc-placeholder">{placeholder}</span>
        )}
        {renderChips()}

        {/* Chevron — fixed to right, gradient so chips don't bleed under it */}
        <span className="msc-chevron">
          <ExpandMoreRoundedIcon style={{ fontSize: 20 }} />
        </span>
      </div>

      {open && ReactDOM.createPortal(dropdownContent, document.body)}
    </>
  );
};

export default MultiSelectChipInput;
